import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader, Save, X } from 'lucide-react';
import { fieldClass, ghostBtn, labelClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import { emptyOmniChatForm, formFromAgent, OMNICHAT_ICON_OPTIONS } from './omniChatConstants';
import OmniChatGatewayFields from './OmniChatGatewayFields';
import { logOmniChat } from './omniChatLogger';
import { STARTER_INSTRUCTIONS } from './omniChatSynthetic';
import { useOmniChat } from './omniChatStore';
import type { OmniChatAgent, OmniChatFormState } from './omniChatTypes';

export default function OmniChatAgentDialog({
  agent,
  isLight,
  open,
  saving,
  onClose,
  onSave,
}: {
  agent?: OmniChatAgent;
  isLight: boolean;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (form: OmniChatFormState) => void;
}) {
  const { gateways, inferenceKeys } = useOmniChat();
  const [form, setForm] = useState<OmniChatFormState>(() =>
    agent ? formFromAgent(agent) : emptyOmniChatForm(gateways[0]?.id ?? '')
  );

  useEffect(() => {
    if (!open) return;
    setForm(agent ? formFromAgent(agent) : emptyOmniChatForm(gateways[0]?.id ?? ''));
    logOmniChat('app', agent ? 'dialog_edit_open' : 'dialog_create_open', { id: agent?.id });
  }, [agent, open]);

  useEffect(() => {
    if (!open || agent || form.gatewayId || !gateways[0]) return;
    setForm((prev) => ({ ...prev, gatewayId: gateways[0].id }));
  }, [agent, form.gatewayId, gateways, open]);

  if (!open) return null;

  const patch = (next: Partial<OmniChatFormState>) => setForm((prev) => ({ ...prev, ...next }));
  const ink = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className={`text-lg font-semibold ${ink}`}>New agent</h2>
            <p className={`text-sm mt-1 ${muted}`}>Title, instruction, and gateway.</p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className={labelClass(isLight)}>Title</span>
            <input
              className={fieldClass(isLight)}
              value={form.title}
              placeholder="Benefits Concierge"
              onChange={(event) => patch({ title: event.target.value })}
            />
          </label>
          <label className="block">
            <span className={labelClass(isLight)}>Description</span>
            <textarea
              className={`${fieldClass(isLight)} min-h-[72px] resize-y`}
              value={form.description}
              placeholder="What this agent is for."
              onChange={(event) => patch({ description: event.target.value })}
            />
          </label>
          <div>
            <p className={labelClass(isLight)}>Icon</p>
            <div className="flex flex-wrap gap-2">
              {OMNICHAT_ICON_OPTIONS.map((option) => {
                const Icon = option.icon;
                const active = form.iconId === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    title={option.label}
                    aria-label={option.label}
                    onClick={() => patch({ iconId: option.id })}
                    className={`h-9 w-9 rounded-lg flex items-center justify-center border ${
                      active
                        ? isLight
                          ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                          : 'border-indigo-500/40 bg-indigo-500/15 text-indigo-300'
                        : isLight
                          ? 'border-neutral-200 text-neutral-500 hover:bg-neutral-50'
                          : 'border-neutral-800 text-neutral-400 hover:bg-neutral-800'
                    }`}
                  >
                    <Icon size={15} />
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className={labelClass(isLight)}>Agent instruction</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {STARTER_INSTRUCTIONS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => patch({ instruction: chip })}
                  className={`rounded-full px-2.5 py-1 text-[11px] ${
                    form.instruction === chip
                      ? isLight
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'bg-indigo-500/15 text-indigo-200'
                      : isLight
                        ? 'bg-neutral-100 text-neutral-600'
                        : 'bg-neutral-800 text-neutral-300'
                  }`}
                >
                  {chip.length > 36 ? `${chip.slice(0, 36)}…` : chip}
                </button>
              ))}
            </div>
            <textarea
              className={`${fieldClass(isLight)} min-h-[110px] resize-y`}
              value={form.instruction}
              placeholder="You help employees with…"
              onChange={(event) => patch({ instruction: event.target.value })}
            />
          </div>

          <OmniChatGatewayFields
            form={form}
            gateways={gateways}
            inferenceKeys={inferenceKeys}
            isLight={isLight}
            onPatch={patch}
          />
        </div>

        <div className={`px-5 py-4 border-t flex justify-end gap-2 ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} disabled={saving} onClick={() => onSave(form)}>
            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
